import type { Editor } from "@tiptap/core";
import { tableAround } from "../chapter_markdown/table_nodes";
import { belowOf, type MenuGroup } from "./floating";
import { button, element } from "./dom";
import type { Caption } from "./table_view";

const BUTTON = "text-link underline hover:no-underline disabled:text-ink-faint disabled:no-underline";

export interface TableBar {
	el: HTMLElement;
	// Enables and presses the buttons for the selection as it is now.
	sync: () => void;
}

// "Tabela: + Linha · + Coluna · Mesclar · Dividir · Legenda · Remover ▾", over the table the caret is in.
export function tableBar(editor: Editor, t: (key: string) => string, menu: (at: { left: number; top: number }, groups: MenuGroup[]) => void): TableBar {
	const el = element("div", "absolute z-10 flex flex-wrap items-center gap-2 rounded border border-line bg-surface-raised px-2 py-0.5 text-sm shadow");
	el.setAttribute("role", "toolbar");
	el.setAttribute("aria-label", t("table_tools"));
	const run = (command: () => boolean) => () => void command();
	const chain = () => editor.chain().focus();
	const caption = () => {
		const found = tableAround(editor.state.selection);
		if (!found) return;
		const next: Caption | null = found.node.attrs.caption ? null : { title: t("table_caption_default"), number: null };
		editor.view.dispatch(editor.state.tr.setNodeMarkup(found.pos, undefined, { ...found.node.attrs, caption: next }));
		if (next) (editor.view.nodeDOM(found.pos) as HTMLElement | null)?.querySelector<HTMLInputElement>(".odo-chapter-caption__title")?.select();
	};
	const addRow = button(
		BUTTON,
		t("table_add_row"),
		run(() => chain().addRowAfter().run()),
	);
	const addColumn = button(
		BUTTON,
		t("table_add_column"),
		run(() => chain().addColumnAfter().run()),
	);
	const merge = button(
		BUTTON,
		t("table_merge"),
		run(() => chain().mergeCells().run()),
	);
	const split = button(
		BUTTON,
		t("table_split"),
		run(() => chain().splitCell().run()),
	);
	const legend = button(`${BUTTON} aria-pressed:font-bold`, t("table_caption"), caption);
	const remove = button(BUTTON, `${t("remove")} ▾`, () =>
		menu(belowOf(remove), [
			{
				items: [
					{ label: t("table_remove_row"), run: () => void chain().deleteRow().run() },
					{ label: t("table_remove_column"), run: () => void chain().deleteColumn().run() },
					{ label: t("table_remove_table"), run: () => void chain().deleteTable().run() },
				],
			},
		]),
	);
	el.append(element("span", "text-ink-muted", t("table_label")), addRow, addColumn, merge, split, legend, remove);
	const sync = () => {
		merge.disabled = !editor.can().mergeCells();
		split.disabled = !editor.can().splitCell();
		legend.setAttribute("aria-pressed", String(Boolean(tableAround(editor.state.selection)?.node.attrs.caption)));
	};
	sync();
	return { el, sync };
}
